import { ReactElement, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { LoadingButton } from '@mui/lab'
import { Button, List, ListItem, ListItemText, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import DialogBase from '@ui/feedback/DialogBase'
import { Errors, Form2 } from '@ui/forms'
import { closeSnackbar, enqueueSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { useResultsInvalidation } from '@editorHooks'
import { getElementLoads, getModel, getVerticalTransmissionGraph } from '@queries'
import {
  createBeam,
  createPurlin,
  updateBeam,
  updateElementCrossSectionAssignment,
  updatePurlin,
} from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import SplitLineElementFormFields from './SplitLineElementFormFields'
import { splitLineElementSchema } from './schema'
import { splitLineElement } from './utils'

type LineElementType = 'beams' | 'purlins'

interface Props {
  elementGuid: string
  elementType: LineElementType
  interactionMode: '2D' | '3D'
  setElementStale: (element: PlanLineElement) => void
}

interface SplitFormValues {
  splitPoint: { x: number; y: number; z: number }
  projectInXY: boolean
  element: PlanLineElement
}

const ELEMENT_COPY: Record<
  LineElementType,
  { pending: string; success: string; error: string; confirmTitle: string; confirmBody: string }
> = {
  beams: {
    pending: 'Unterzug wird geteilt. Dies kann einige Sekunden dauern.',
    success: 'Unterzug erfolgreich geteilt',
    error: 'Fehler beim Teilen des Unterzugs',
    confirmTitle: 'Unterzug teilen bestätigen',
    confirmBody: 'Möchten Sie den Unterzug wirklich teilen?',
  },
  purlins: {
    pending: 'Pfette wird geteilt. Dies kann einige Sekunden dauern.',
    success: 'Pfette erfolgreich geteilt',
    error: 'Fehler beim Teilen der Pfette',
    confirmTitle: 'Pfette teilen bestätigen',
    confirmBody: 'Möchten Sie die Pfette wirklich teilen?',
  },
}

const SIDE_EFFECTS = [
  'Hinzugefügte Lasten',
  'Lastweiterleitung (Vertikal und Zug)',
  'Bauteilname des neuen Teils wird neu vergeben',
]

const SplitLineElementForm = ({
  elementGuid,
  elementType,
  interactionMode,
  setElementStale,
}: Props): ReactElement | null => {
  const { projectId }: { projectId?: string } = useParams()
  const client = useQueryClient()
  const invalidateResults = useResultsInvalidation()
  const beams = useModelStore(state => state.model.beams)
  const purlins = useModelStore(state => state.model.purlins)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<SplitFormValues | null>(null)

  const copy = ELEMENT_COPY[elementType]
  const snackbarWaitItemId = `split-line-element-form-${elementGuid}-pending-notification`

  const selectedElement = useMemo(() => {
    const elements = elementType === 'beams' ? beams : purlins
    return elements.find(element => element.guid === elementGuid)
  }, [beams, elementGuid, elementType, purlins])

  const defaultValues = useMemo((): SplitFormValues | null => {
    if (!selectedElement) return null

    const { start, end } = selectedElement.shape

    return {
      splitPoint: {
        x: (start.x + end.x) / 2,
        y: (start.y + end.y) / 2,
        z: (start.z + end.z) / 2,
      },
      projectInXY: interactionMode === '2D',
      element: selectedElement,
    }
  }, [interactionMode, selectedElement])

  const { mutate, isLoading } = useMutation(
    async (formData: SplitFormValues) => {
      enqueueSnackbar(copy.pending, {
        variant: 'info',
        persist: true,
        key: snackbarWaitItemId,
      })

      const splitData = splitLineElement(
        formData.element,
        formData.splitPoint,
        formData.projectInXY,
      )

      if (elementType === 'beams') {
        await updateBeam.request(projectId as string, splitData.updatedElement)
        await createBeam.request(projectId as string, splitData.createdElement)
      } else {
        await updatePurlin.request(projectId as string, splitData.updatedElement)
        await createPurlin.request(projectId as string, splitData.createdElement)
      }

      if (formData.element.crossSection) {
        await updateElementCrossSectionAssignment.request(projectId as string, {
          element_guid: splitData.createdElement.guid,
          element_cs: formData.element.crossSection,
        })
      }

      return splitData
    },
    {
      onSuccess: async data => {
        closeSnackbar(snackbarWaitItemId)
        setElementStale(data.updatedElement)
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getElementLoads.getKey(projectId as string)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar(copy.success, { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        closeSnackbar(snackbarWaitItemId)
        enqueueSnackbar(buildErrorMessage(error, copy.error), { variant: 'error' })
      },
    },
  )

  const handleSubmit = (values: SplitFormValues) => {
    setPendingFormData(values)
    setShowConfirmDialog(true)
  }

  const handleConfirm = () => {
    if (pendingFormData) {
      setShowConfirmDialog(false)
      mutate(pendingFormData)
      setPendingFormData(null)
    }
  }

  const handleCancel = () => {
    setShowConfirmDialog(false)
    setPendingFormData(null)
  }

  if (!selectedElement || !defaultValues) {
    return null
  }

  return (
    <>
      <Form2<SplitFormValues>
        id="SplitLineElementForm"
        key={elementGuid}
        defaultValues={defaultValues}
        validationSchema={splitLineElementSchema}
        onSubmit={handleSubmit}
      >
        <SplitLineElementFormFields isLoading={isLoading} interactionMode={interactionMode} />
        <Errors />
      </Form2>

      <DialogBase open={showConfirmDialog} onClose={handleCancel} title={copy.confirmTitle}>
        <>
          <Typography variant="body1" gutterBottom>
            {copy.confirmBody}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            Folgende Einstellungen werden zurückgesetzt:
          </Typography>
          <List dense>
            {SIDE_EFFECTS.map((effect, index) => (
              <ListItem key={effect} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={`${index + 1}. ${effect}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </>
        <>
          <Button onClick={handleCancel} size="small">
            Abbrechen
          </Button>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            autoFocus
            size="small"
          >
            Bestätigen
          </LoadingButton>
        </>
      </DialogBase>
    </>
  )
}

export default SplitLineElementForm
